import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { VehicleName } from 'shared-ui/lib/components/VehicleName';
import { TableColumn } from 'shared-ui/lib/customs/react/generic-table/TableColumn';
import { formatDate } from 'shared-ui/lib/utils/format-date';
import { routesConfig } from '../config/routes.config';
import { PartnerDashboardFaultsQuery } from '../generated/graphql';

type FaultRow = PartnerDashboardFaultsQuery['partnerDashboardFaults']['list'][0];

const DESCRIPTION_MAX_LENGTH = 60;

const FaultCreatedAtCell = ({ fault }: { fault: FaultRow }): JSX.Element => {
  const { t } = useTranslation('ServiceCallDashboardScene');
  const date = formatDate(fault.createdAt);

  if (fault.isRead === false) {
    return (
      <div className="flex items-center">
        <span className="mr-2">{date}</span>
        <span className="bg-primary text-white text-xs py-1 px-3 rounded-md">
          {t('unreadBadge')}
        </span>
      </div>
    );
  }

  return <>{date}</>;
};

const FaultStatusCell = ({ fault }: { fault: FaultRow }): JSX.Element => {
  const { t } = useTranslation('ServiceCallDashboardScene');

  return <>{fault.isDone ? t('statusDone') : t('statusOpen')}</>;
};

const businessName: TableColumn = {
  field: 'businessName',
  text: 'ServiceCallDashboardScene:businessName',
  sort: true,
};

const vehicle: TableColumn = {
  field: 'vehicleName',
  text: 'ServiceCallDashboardScene:vehicle',
  sort: true,
  default: '-',
  formatter: (data: FaultRow) => <VehicleName vehicle={data.vehicle} />,
};

const description: TableColumn = {
  field: 'description',
  text: 'ServiceCallDashboardScene:faultDescription',
  sort: true,
  formatter: (data: FaultRow) => {
    if (!data.description) {
      return '';
    }

    const text = data.description.trim();

    if (text.length <= DESCRIPTION_MAX_LENGTH) {
      return text;
    }

    return `${text.slice(0, DESCRIPTION_MAX_LENGTH)}...`;
  },
};

const createdAt: TableColumn = {
  field: 'createdAt',
  text: 'ServiceCallDashboardScene:createdAt',
  sort: true,
  formatter: (data: FaultRow) => <FaultCreatedAtCell fault={data} />,
};

const status: TableColumn = {
  field: 'isDone',
  text: 'ServiceCallDashboardScene:status',
  sort: true,
  formatter: (data: FaultRow) => <FaultStatusCell fault={data} />,
};

const linkToFault: TableColumn = {
  field: 'id',
  text: 'General:link',
  formatter: (data: FaultRow) => (
    <Link
      to={routesConfig.SERVICE_CALL.FAULT_SHOW.replace(':id', data.id)}
      className="hover:text-primary text-gray-color"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M14 5l7 7m0 0l-7 7m7-7H3"
        />
      </svg>
    </Link>
  ),
};

export const dashboardFaultColumns = {
  businessName,
  vehicle,
  description,
  createdAt,
  status,
  linkToFault,
};
